import { REJECT, array, nullable, num, object, pureTransform, str, unknownRecord, type FieldParser, type Received } from 'validation';

import { getRecord } from 'api/shared/getRecord';

/**
 * A single connection as returned by nap-backend
 * `GET /connections/:connection_id` (routes/connections.js — secrets removed,
 * `workers` + `folder_path` attached). This is the edit-relevant projection
 * re-derived from the backend source; the endpoint also returns `created_at`,
 * `updated_at` and `folder_path`, which this modal does not read, so the object
 * parser drops them. Assignable to the slice `Connection` shape the modal
 * accepts. Deeply readonly — validated wire data.
 */
export interface FetchedConnection {
  readonly connection_id: number;
  readonly connection_name: string;
  readonly connection_type: string;
  readonly connection_parameters: Record<string, unknown>;
  // Mutable-outer: the modal assigns workers straight into its form state.
  // `worker_connections.priority` is a nullable column with NO default, and the route selects `wc.*` raw.
  readonly workers: { readonly worker_id: number; readonly worker_name: string; readonly priority: number | null }[];
  readonly folder_id: number | null;
}

// `connection_parameters` is per-connection-type dynamic JSON. The wire sends
// `null` for a connection that has none, which is the documented empty rather
// than a value we could not read — so it maps to `{}` with NO warning. Reading
// it as drift marked the whole record degraded, and the edit dialog then refused
// to open on it: the same defect this issue fixed for `getEvent`'s process
// events, in the sibling endpoint.
//
// Unlike `getEvent`, the TYPE stays non-null. The sub-forms dereference it
// directly (`connectionData.connection_parameters.sub_type`), and
// `strictNullChecks` is off, so widening it would be a runtime break tsc could
// not see. Anything that is neither null nor an object is still drift, and still
// usable as `{}`.
const dynamicJson = (): FieldParser<Record<string, unknown>> => pureTransform(nullable(unknownRecord()), (value) => value ?? {});

const connectionSchema = object<FetchedConnection>({
  connection_id: num(REJECT),
  connection_name: str(),
  connection_type: str(),
  // Per-connection-type dynamic JSON (secrets stripped server-side); the server
  // is the validation authority and the modal hands it to type-specific
  // sub-forms that dereference it. Validated only as "an object" (contents pass
  // through verbatim); a missing/null payload defaults to `{}` so the sub-forms
  // never dereference null.
  connection_parameters: dynamicJson(),
  workers: array(
    object<{ worker_id: number; worker_name: string; priority: number | null }>({ worker_id: num(), worker_name: str(), priority: nullable(num()) })
  ),
  folder_id: nullable(num()),
});

/**
 * GET `/connections/:id` — one record for the edit dialog, so it works
 * when launched from the Workspace (which never populates the list slice) and
 * never opens on a loosely-cast store row.
 *
 * Boundary-validated. See `getRecord` for what each outcome means; the short
 * version is that `null` means the server said there is no such record, and
 * the failure arm means it said something we could not read. The caller must
 * tell those apart, because refreshing a list helps with only one of them.
 */
export const getConnection = (id: number): Promise<Received<FetchedConnection | null>> =>
  getRecord(`/connections/${id}`, { schema: connectionSchema, source: 'getConnection' });
